import { Injectable } from '@angular/core';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import {
    IOwnershipArea,
    IOwnershipBusinessUnit,
    IOwnershipUser,
    ITransferOwnershipAvailableContent,
} from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    FiTransferOwnershipConfirmDialogComponent,
    IFiTransferOwnershipConfirmDialogComponentData,
} from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership-confirm-dialog/fi-transfer-ownership-confirm-dialog.component';
import { ISelectHierarchyItem } from '@fi-shared/fi-select-search/fi-select-search';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';

@Injectable()
export class FiTransferOwnershipHelperService {
    readonly transferOwnershipObjectTypes: ObjectTypes[] = [
        ObjectTypes.Document,
        ObjectTypes.Template,
        ObjectTypes.MediaBank,
        ObjectTypes.Form,
        ObjectTypes.Task,
        ObjectTypes.Forum,
        ObjectTypes.CheckList,
        ObjectTypes.Checklist2,
        ObjectTypes.Checklist2Template,
        ObjectTypes.ChecklistSchedule,
        ObjectTypes.Checklist2Audit,
        ObjectTypes.Checklist2AuditTemplate,
        ObjectTypes.Checklist2AuditSchedule,
        ObjectTypes.Onboarding,
        ObjectTypes.Quiz,
        ObjectTypes.TodoList,
    ];

    private readonly customNamesObjectTypes: ObjectTypes[] = [ObjectTypes.Template, ObjectTypes.Checklist2Template];

    getCustomContentTitle(objectType: ObjectTypes, isPlural: boolean): string {
        if (objectType === ObjectTypes.Template)
            return this.appTextService.get(isPlural ? 'document.page_templates' : 'common.page_template');
        if (objectType === ObjectTypes.Checklist2Template)
            return this.appTextService.get(
                isPlural ? 'checklist.checklist_templates_v2' : 'checklist.checklist_template_v2_ownership',
            );
        return '';
    }

    constructor(
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
    ) {}

    getContentTitle(objectType: ObjectTypes, includeDescription: boolean, isPlural: boolean): string {
        if (this.customNamesObjectTypes.includes(objectType)) return this.getCustomContentTitle(objectType, isPlural);

        const textId = getObjectTypeNameTextId(objectType, isPlural);
        let text = this.appTextService.get(textId);
        if (includeDescription) {
            if (objectType === ObjectTypes.Forum) {
                text += ` (<i>${this.appTextService.get('forum.forum_posts_not_included')}</i>)`;
            }
        }
        return text;
    }

    getAvailableTransferOwnershipContents(availableObjectTypes: ObjectTypes[]): ITransferOwnershipAvailableContent[] {
        let transferOwnershipObjectTypes = this.transferOwnershipObjectTypes;
        const hasAccessToOnboardings = this.accessToApplicationTypeService.hasCustomerAccessToModule(
            ModuleType.Onboarding,
        );
        if (!hasAccessToOnboardings) {
            const onboardingObjectTypes = [ObjectTypes.Onboarding, ObjectTypes.Quiz, ObjectTypes.TodoList];
            transferOwnershipObjectTypes = transferOwnershipObjectTypes.filter(
                (objectType) => !onboardingObjectTypes.includes(objectType),
            );
        }

        const hasAccessToTasksV2 = this.accessToApplicationTypeService.hasCustomerAccessToModule(ModuleType.TaskV2);
        const availableTransferOwnershipContents = transferOwnershipObjectTypes.map((objectType: ObjectTypes) => {
            const normalizedObjectType =
                objectType === ObjectTypes.Task && hasAccessToTasksV2 ? ObjectTypes.Task2 : objectType;
            return {
                objectType: normalizedObjectType,
                isSelected: false,
                isDisabled: !availableObjectTypes.includes(normalizedObjectType),
            };
        });
        return availableTransferOwnershipContents;
    }

    getTransferOwnershipDefaultContent(): ITransferOwnershipAvailableContent[] {
        return this.getAvailableTransferOwnershipContents([]);
    }

    showTransferOwnershipConfirmationDialog(contentData: IFiTransferOwnershipConfirmDialogComponentData) {
        return this.fiDialogsService.custom({
            contentComponentRef: FiTransferOwnershipConfirmDialogComponent,
            contentComponentData: contentData,
            isBigSizeTopBorder: true,
            headerIconClass: 'orange far fa-circle-exclamation',
            headerText: this.appTextService.get('ownership.confirm_transfer_header'),
            colorClass: 'orange',
            yesButtonText: this.appTextService.get('common.confirm'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '650px',
            },
        });
    }

    mapOwnershipHierarchy(ownershipAreas: IOwnershipArea[]): ISelectHierarchyItem[] {
        const mapUser = (
            user: IOwnershipUser,
            level: number,
            parentId: number,
            parentObjectType: ObjectTypes,
        ): ISelectHierarchyItem => ({
            Id: user.Id,
            ObjectType: ObjectTypes.User,
            Name: user.FullName,
            ParentId: parentId,
            ParentObjectType: parentObjectType,
            Level: level,
            ChildItems: [],
        });

        const mapBusinessUnit = (
            businessUnit: IOwnershipBusinessUnit,
            level: number,
            parentId: number,
            parentObjectType: ObjectTypes,
        ): ISelectHierarchyItem => ({
            Id: businessUnit.Id,
            ObjectType: ObjectTypes.BusinessUnit,
            Name: businessUnit.Name,
            ParentId: parentId,
            ParentObjectType: parentObjectType,
            Level: level,
            ChildItems: businessUnit.Users.map((user) =>
                mapUser(user, level + 1, businessUnit.Id, ObjectTypes.BusinessUnit),
            ),
        });

        const mapArea = (
            area: IOwnershipArea,
            level: number,
            parentId: number,
            parentObjectType: ObjectTypes,
        ): ISelectHierarchyItem => ({
            Id: area.Id,
            ObjectType: ObjectTypes.Area,
            Name: area.Name,
            ParentId: parentId,
            ParentObjectType: parentObjectType,
            Level: level,
            ChildItems: area.ChildItems.map((childArea) =>
                mapArea(childArea, level + 1, area.Id, ObjectTypes.Area),
            ).concat(
                area.BusinessUnits.map((businessUnit) =>
                    mapBusinessUnit(businessUnit, level + 1, area.Id, ObjectTypes.Area),
                ),
            ),
        });

        return ownershipAreas.map((ownershipArea) => mapArea(ownershipArea, 1, undefined, undefined));
    }
}
